import React from 'react';
import useMetaTags from "../hooks/useMetaTags";
import {
  FileText,
  CheckCircle2,
  Users,
  ShoppingCart,
  CreditCard,
  Truck,
  Copyright,
  Ban,
  MessageSquareText,
  MessageCircle,
  ShieldAlert,
  Gavel,
  Handshake,
  LogOut,
  Scale,
  RefreshCw,
  Mail,
} from 'lucide-react';

const LAST_UPDATED = "July 18, 2026";

const Section = ({ icon: Icon, title, children }) => (
  <div className="mb-10">
    <h2 className="flex items-center gap-3 text-2xl font-bold text-gray-800 mb-4">
      <span className="p-2 bg-[#679729]/10 rounded-lg text-[#679729]">
        <Icon size={20} />
      </span>
      {title}
    </h2>
    <div className="text-gray-700 leading-relaxed space-y-3 pl-1">
      {children}
    </div>
  </div>
);

const TermsOfService = () => {
  useMetaTags({
    title: "Terms of Service | Organic Pads",
    description: "The Terms of Service governing your use of the Organic Pads website and the purchase of products from OrganicPads.",
    keywords: "organic pads terms of service, terms and conditions, organicpads terms",
    author: "Organic Pads",
    robots: "index, follow",
    canonical: "/terms-of-service",
    ogTitle: "Terms of Service | Organic Pads",
    ogDescription: "The terms that govern your use of the Organic Pads website and orders.",
    ogImage: "https://organicpads.shop/logo.png",
    ogUrl: "https://organicpads.shop/terms-of-service",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "Terms of Service | Organic Pads",
    twitterDescription: "The terms that govern your use of the Organic Pads website and orders.",
    twitterImage: "https://organicpads.shop/logo.png",
    twitterSite: "@OrganicPads",
    twitterCreator: "@OrganicPads",
  });

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms of Service",
    "url": "https://organicpads.shop/terms-of-service",
    "description": "OrganicPads' Terms of Service governing use of the website and purchase of products.",
    "publisher": { "@type": "Organization", "name": "OrganicPads", "url": "https://organicpads.shop" },
    "dateModified": "2026-07-18",
  };

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 mt-10 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-[#679729] mb-4">
            <FileText size={24} />
            <span className="text-lg font-medium">Please Read Carefully</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Terms of Service</h1>
          <p className="text-gray-500">Last updated: {LAST_UPDATED}</p>
        </div>

        <p className="text-gray-700 leading-relaxed mb-10">
          These Terms of Service ("Terms") govern your use of the website organicpads.shop and any purchase of
          products from OrganicPads ("we", "us", "our"), the MSME-registered business that owns and operates the
          Organic Pads brand, based in Ahmedabad, Gujarat, India. By accessing our website or placing an order, you
          agree to these Terms. If you do not agree, please do not use our website or services.
        </p>

        <Section icon={CheckCircle2} title="1. Acceptance of Terms">
          <p>
            By browsing our website, creating an account, or placing an order, you confirm that you have read,
            understood, and agree to be bound by these Terms, as well as our Privacy Policy and Refund Policy.
          </p>
        </Section>

        <Section icon={Users} title="2. Eligibility">
          <p>
            You must be at least 18 years old, or transacting under the supervision of a parent or legal guardian, to
            place an order on our website.
          </p>
        </Section>

        <Section icon={ShoppingCart} title="3. Use of the Website">
          <p>
            You agree to use our website only for lawful purposes and to provide accurate and complete information
            when creating an account, contacting us, or placing an order. You are responsible for maintaining the
            confidentiality of any account credentials and for all activity under your account.
          </p>
        </Section>

        <Section icon={ShieldAlert} title="4. Product Information, Pricing & Order Acceptance">
          <p>
            We make every effort to ensure product descriptions, images, and prices on our website are accurate.
            However, errors may occasionally occur. We reserve the right to correct any pricing or product
            information errors, and to accept, refuse, limit, or cancel any order, at our discretion — for example,
            in cases of suspected fraud, pricing errors, or stock unavailability. If we cancel an order you have
            already paid for, we will issue a full refund.
          </p>
        </Section>

        <Section icon={CreditCard} title="5. Payment">
          <p>
            We accept payment through the online payment methods made available at checkout (such as cards, UPI, and
            net banking) and, where offered, Cash on Delivery. Full payment (or COD confirmation) is required before
            an order is dispatched.
          </p>
        </Section>

        <Section icon={Truck} title="6. Shipping & Delivery">
          <p>
            We aim to dispatch and deliver orders within the timelines shown at checkout or in your order
            confirmation. Delivery timelines are estimates and may vary due to factors outside our control, such as
            courier delays. Please refer to our Refund Policy for how damaged, defective, or missing items are
            handled.
          </p>
        </Section>

        <Section icon={Copyright} title="7. Intellectual Property">
          <p>
            All content on our website — including the Organic Pads name and logo, product photography, graphics,
            and text — is owned by or licensed to OrganicPads and is protected by applicable intellectual property
            laws. You may not copy, reproduce, or use our content or branding without our prior written permission.
          </p>
        </Section>

        <Section icon={Ban} title="8. Prohibited Conduct">
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Use our website for any unlawful or fraudulent purpose.</li>
            <li>Attempt to gain unauthorized access to our systems or interfere with the website's operation.</li>
            <li>Scrape, copy, or republish our content without permission.</li>
            <li>Infringe on our or any third party's intellectual property rights.</li>
          </ul>
        </Section>

        <Section icon={MessageSquareText} title="9. Reviews & User Content">
          <p>
            If you submit a review, testimonial, or other content to us, you grant OrganicPads a non-exclusive,
            royalty-free, worldwide license to use, reproduce, and display that content in connection with our
            products and marketing, provided it does not contain unlawful, defamatory, or infringing material.
          </p>
        </Section>

        <Section icon={MessageCircle} title="10. Third-Party Links & Services">
          <p>
            Our website may link to third-party websites, and we offer customer support through WhatsApp Business as
            an additional communication channel. WhatsApp Business is provided by Meta Platforms, Inc. and is
            governed by Meta's own terms and policies. We are not responsible for the content, policies, or
            practices of any third-party websites or services.
          </p>
        </Section>

        <Section icon={ShieldAlert} title="11. Disclaimer of Warranties">
          <p>
            Our website and services are provided on an "as is" and "as available" basis. We do not guarantee that
            the website will be uninterrupted, error-free, or completely secure, to the fullest extent permitted by
            applicable law.
          </p>
        </Section>

        <Section icon={Gavel} title="12. Limitation of Liability">
          <p>
            To the fullest extent permitted by applicable law, OrganicPads' total liability arising from your use of
            our website or products shall not exceed the amount you paid for the relevant order, and we shall not be
            liable for any indirect, incidental, or consequential damages.
          </p>
        </Section>

        <Section icon={Handshake} title="13. Indemnification">
          <p>
            You agree to indemnify and hold OrganicPads harmless from any claims, losses, or damages arising from
            your misuse of our website or violation of these Terms.
          </p>
        </Section>

        <Section icon={LogOut} title="14. Termination of Access">
          <p>
            We may suspend or restrict your access to our website at our discretion, including in cases of
            suspected misuse or violation of these Terms.
          </p>
        </Section>

        <Section icon={Scale} title="15. Governing Law & Dispute Resolution">
          <p>
            These Terms are governed by the laws of India. Any disputes arising out of or relating to these Terms
            shall be subject to the exclusive jurisdiction of the courts in Ahmedabad, Gujarat.
          </p>
        </Section>

        <Section icon={RefreshCw} title="16. Changes to These Terms">
          <p>
            We may update these Terms from time to time. Continued use of our website after changes are posted
            constitutes your acceptance of the revised Terms. Please check this page periodically for updates.
          </p>
        </Section>

        <Section icon={Mail} title="17. Contact Information">
          <ul className="list-none space-y-1">
            <li>Email: <a href="mailto:organicpads.shop@gmail.com" className="text-[#679729] hover:underline">organicpads.shop@gmail.com</a></li>
            <li>Phone: <a href="tel:+917203802493" className="text-[#679729] hover:underline">+91 720 3802 493</a></li>
            <li>Address:  Nikol, Ahmedabad, Gujarat 382350</li>
          </ul>
        </Section>
      </div>
      <script type="application/ld+json">
        {JSON.stringify(schemaMarkup)}
      </script>
    </>
  );
};

export default TermsOfService;
